// frontend/src/components/postproduction/SalesVelocityChart.tsx
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { PerformanceIntelligence } from '../../types/PPDash';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

interface SalesVelocityChartProps {
  performances: PerformanceIntelligence[];
}

// Color palette for different performances
const LINE_COLORS = [
  '#4caf50', '#2196f3', '#ff9800', '#9c27b0', '#e91e63',
  '#00bcd4', '#8bc34a', '#ff5722', '#3f51b5', '#cddc39',
  '#009688', '#ffc107', '#795548', '#607d8b', '#ffeb3b'
];

const SalesVelocityChart: React.FC<SalesVelocityChartProps> = ({ performances }) => {
  // Filter performances that have sales velocity data
  const performancesWithVelocity = useMemo(() => {
    return performances.filter(p => p.salesVelocity && p.salesWindow);
  }, [performances]);

  // Find max week across all performances
  const maxWeek = useMemo(() => {
    let max = 0;
    performancesWithVelocity.forEach(perf => {
      if (perf.salesWindow) {
        max = Math.max(max, perf.salesWindow.duration);
      }
    });
    return max;
  }, [performancesWithVelocity]);

  // Initialize all weeks as visible
  const [visibleWeeks, setVisibleWeeks] = useState<{ [key: string]: boolean }>(() => {
    const initial: { [key: string]: boolean } = {};
    for (let i = 1; i <= maxWeek; i++) {
      initial[`week${i}`] = true;
    }
    initial['opening'] = true;
    return initial;
  });

  const handleToggle = (week: string) => {
    setVisibleWeeks(prev => ({
      ...prev,
      [week]: !prev[week]
    }));
  };

  const handleReset = () => {
    const reset: { [key: string]: boolean } = {};
    for (let i = 1; i <= maxWeek; i++) {
      reset[`week${i}`] = true;
    }
    reset['opening'] = true;
    setVisibleWeeks(reset);
  };

  const handleShowOpeningVsFinal = () => {
    const openingFinal: { [key: string]: boolean } = {};
    for (let i = 1; i <= maxWeek; i++) {
      openingFinal[`week${i}`] = i === 1; // Final week only
    }
    openingFinal['opening'] = true; // Opening week
    setVisibleWeeks(openingFinal);
  };

  // Prepare chart data - one line per performance
  const chartData = useMemo(() => {
    const dataByWeek: { [key: string]: any } = {};

    // Initialize weeks
    for (let i = maxWeek; i >= 1; i--) {
      const weekKey = i === 1 ? 'Final Week' : `Week ${i}`;
      dataByWeek[weekKey] = { week: weekKey, weekNum: i };
    }

    // Add opening week
    dataByWeek['Opening'] = { week: 'Opening', weekNum: maxWeek + 1 };

    // Add data for each performance
    performancesWithVelocity.forEach((perf, index) => {
      const perfName = `${perf.name.substring(0, 20)}${perf.name.length > 20 ? '...' : ''}`;
      const velocity = perf.salesVelocity;
      const duration = perf.salesWindow?.duration || 0;

      if (velocity) {
        // Add weekly data
        for (let i = 1; i <= duration; i++) {
          const weekKey = i === 1 ? 'Final Week' : `Week ${i}`;
          const velocityKey = `week${i}`;
          
          if (visibleWeeks[velocityKey] && dataByWeek[weekKey]) {
            dataByWeek[weekKey][perfName] = velocity[velocityKey] || 0;
          }
        }

        // Add opening week data (same as highest week number)
        const openingWeekKey = `week${duration}`;
        if (visibleWeeks['opening'] && dataByWeek['Opening']) {
          dataByWeek['Opening'][perfName] = velocity[openingWeekKey] || 0;
        }
      }
    });

    // Convert to array and sort by week number (descending - opening first)
    return Object.values(dataByWeek)
      .sort((a, b) => b.weekNum - a.weekNum);
  }, [performancesWithVelocity, visibleWeeks, maxWeek]);

  // Calculate sale window insights
  const insights = useMemo(() => {
    const byDuration: { [key: number]: { performances: number; totalTickets: number; avgTickets: number } } = {};
    let totalOpeningWeekSales = 0;
    let totalFinalWeekSales = 0;
    let totalAllSales = 0;

    performancesWithVelocity.forEach(perf => {
      const duration = perf.salesWindow?.duration || 0;
      const velocity = perf.salesVelocity;
      
      if (!velocity) return;

      // Group by sale window duration
      if (!byDuration[duration]) {
        byDuration[duration] = { performances: 0, totalTickets: 0, avgTickets: 0 };
      }

      // Count total tickets sold
      let perfTotal = 0;
      for (let i = 1; i <= duration; i++) {
        const weekKey = `week${i}`;
        const weekValue = velocity[weekKey];
        const weekSales = typeof weekValue === 'number' ? weekValue : 0;
        perfTotal += weekSales;
        totalAllSales += weekSales;

        if (i === 1) totalFinalWeekSales += weekSales;
        if (i === duration) totalOpeningWeekSales += weekSales;
      }

      byDuration[duration].performances += 1;
      byDuration[duration].totalTickets += perfTotal;
    });

    // Calculate averages
    Object.keys(byDuration).forEach(duration => {
      const data = byDuration[parseInt(duration)];
      data.avgTickets = Math.round(data.totalTickets / data.performances);
    });

    const openingPct = totalAllSales > 0 ? ((totalOpeningWeekSales / totalAllSales) * 100).toFixed(1) : 0;
    const finalPct = totalAllSales > 0 ? ((totalFinalWeekSales / totalAllSales) * 100).toFixed(1) : 0;

    return {
      byDuration,
      openingPct,
      finalPct,
      totalSales: totalAllSales
    };
  }, [performancesWithVelocity]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1.5, backgroundColor: 'background.paper' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: entry.color,
                  borderRadius: '50%'
                }}
              />
              <Typography variant="caption">
                {entry.name}: {entry.value} tickets
              </Typography>
            </Box>
          ))}
        </Paper>
      );
    }
    return null;
  };

  const activeWeeksCount = Object.values(visibleWeeks).filter(v => v).length;
  const performanceNames = performancesWithVelocity.map(p => 
    `${p.name.substring(0, 20)}${p.name.length > 20 ? '...' : ''}`
  );

  if (performancesWithVelocity.length === 0) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Sales Velocity Analysis
        </Typography>
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: 'background.paper' }}>
          <Typography color="text.secondary">
            No sales velocity data available for the selected performances
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Sales Velocity Analysis
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={handleShowOpeningVsFinal}
            variant="outlined"
            size="small"
          >
            Opening vs Final
          </Button>
          <Button
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
            variant="outlined"
            size="small"
          >
            Show All Weeks
          </Button>
        </Box>
      </Box>

      {/* Sale Window Intelligence */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: 'background.paper' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Opening Week Impact
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {insights.openingPct}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                of total sales
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: 'background.paper' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Final Week Impact
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {insights.finalPct}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                of total sales
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: 'background.paper' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Tracked Sales
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {insights.totalSales}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                tickets across all shows
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sale Window Duration Insights */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.paper' }}>
        <Typography variant="subtitle2" gutterBottom>
          Sale Window Duration Analysis:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
          {Object.keys(insights.byDuration)
            .sort((a, b) => parseInt(b) - parseInt(a))
            .map(duration => {
              const data = insights.byDuration[parseInt(duration)];
              return (
                <Box key={duration}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {duration}-week windows
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {data.performances} show{data.performances !== 1 ? 's' : ''} • 
                    Avg {data.avgTickets} tickets
                  </Typography>
                </Box>
              );
            })}
        </Box>
      </Paper>

      <Paper sx={{ p: 2, backgroundColor: 'background.paper' }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Filter by Week:
          </Typography>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleWeeks['opening']}
                  onChange={() => handleToggle('opening')}
                  size="small"
                />
              }
              label={<Typography variant="body2">Opening Week</Typography>}
            />
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            {Array.from({ length: maxWeek }, (_, i) => maxWeek - i).map(weekNum => (
              <FormControlLabel
                key={weekNum}
                control={
                  <Checkbox
                    checked={visibleWeeks[`week${weekNum}`]}
                    onChange={() => handleToggle(`week${weekNum}`)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    {weekNum === 1 ? 'Final Week' : `Week ${weekNum}`}
                  </Typography>
                }
              />
            ))}
          </FormGroup>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {activeWeeksCount === 0 ? 'Select at least one week to display' : 
             `Showing ${activeWeeksCount} week${activeWeeksCount !== 1 ? 's' : ''}`}
          </Typography>
        </Box>

        {activeWeeksCount === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Please select at least one week to display the chart
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#444" />
              <XAxis 
                dataKey="week"
                tick={{ fill: '#aaa', fontSize: 12 }}
              />
              <YAxis 
                label={{ value: 'Tickets Sold', angle: -90, position: 'insideLeft', fill: '#aaa' }}
                tick={{ fill: '#aaa' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ paddingTop: '20px' }} />
              {performanceNames.map((name, index) => (
                <Line
                  key={name}
                  type="monotone"
                  dataKey={name}
                  stroke={LINE_COLORS[index % LINE_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Paper>
    </Box>
  );
};

export default SalesVelocityChart;